import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { DLPProvider } from './DLPProvider';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Truck,
  Building2,
  Calculator,
  DollarSign,
  FileText,
  Languages,
  User,
  Moon,
  CreditCard,
  Sun,
  Menu,
  Wrench,
  Shield,
  GraduationCap
} from 'lucide-react';
import { cn } from '../lib/utils';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isTrainee, canAccessTraining, canAccessCredit, updateLanguage } = useAuth();
  
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check if user is trainee-only (no credit access)
  const isTraineeOnly = isTrainee() && !canAccessCredit();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    // Use updateLanguage to persist to backend
    updateLanguage(newLang);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Main nav - show dashboard only for credit users, training for trainees
  const mainNavItems = isTraineeOnly ? [
    { icon: GraduationCap, label: 'Training Academy', path: '/training' },
  ] : [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Clientes', path: '/clients' },
  ];

  // App nav - only show to users who can access credit (NOT trainees)
  const appNavItems = canAccessCredit() && !isTraineeOnly ? [
    { icon: Truck, label: 'Tasación de Activos', path: '/apps/appraisal', color: 'text-blue-600' },
    { icon: Building2, label: 'Análisis Bancario', path: '/apps/bank-analysis', color: 'text-emerald-600' },
    { icon: CreditCard, label: 'Análisis de Crédito', path: '/apps/credit-report', color: 'text-cyan-600' },
    { icon: Calculator, label: 'Evaluación Crediticia', path: '/apps/credit-evaluation', color: 'text-purple-600' },
    { icon: DollarSign, label: 'Precios', path: '/apps/pricing', color: 'text-amber-600' },
    { icon: FileText, label: 'Resumen', path: '/apps/summary', color: 'text-red-600' },
    { icon: DollarSign, label: 'Deal Pipeline', path: '/deals', color: 'text-red-500' },
  ] : [];

  // Training nav - show to trainees and admins
  const trainingNavItems = canAccessTraining() ? [
    { icon: GraduationCap, label: 'Training Academy', path: '/training', color: 'text-indigo-600' },
  ] : [];

  const adminNavItems = isAdmin() ? [
    { icon: Users, label: 'Usuarios', path: '/users' },
    { icon: Settings, label: 'Config. Tiers', path: '/admin/tiers' },
    { icon: Shield, label: 'Seguridad', path: '/admin/security', color: 'text-red-600' }
  ] : [];

  const isActive = (path) => location.pathname.startsWith(path);

  const roleLabel = {
    admin: 'Administrador',
    manager: 'Manager',
    analyst: 'Analista'
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="main-layout">
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 z-40 h-screen bg-card border-r border-border transition-all duration-300 flex flex-col",
          collapsed ? "w-16" : "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <Link to={isTraineeOnly ? "/training" : "/dashboard"} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                {isTraineeOnly ? (
                  <GraduationCap className="w-4 h-4 text-white" />
                ) : (
                  <Wrench className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-bold text-lg font-['Manrope'] tracking-tight">
                {isTraineeOnly ? 'Training Academy' : 'ICA Tools'}
              </span>
            </Link>
          )}
          {collapsed && (
            <Link to={isTraineeOnly ? "/training" : "/dashboard"} className="mx-auto">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                {isTraineeOnly ? (
                  <GraduationCap className="w-4 h-4 text-white" />
                ) : (
                  <Wrench className="w-4 h-4 text-white" />
                )}
              </div>
            </Link>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {/* Main Nav */}
            {mainNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                  isActive(item.path) 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            ))}

            {/* Apps Section - Only for non-trainee users */}
            {appNavItems.length > 0 && (
              <>
                {!collapsed && (
                  <div className="pt-4 pb-2">
                    <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Herramientas
                    </p>
                  </div>
                )}
                {collapsed && <Separator className="my-2" />}

                {appNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                      isActive(item.path) 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("w-5 h-5 flex-shrink-0", !isActive(item.path) && item.color)} />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                ))}
              </>
            )}

            {/* Training Section - Only for non-trainee users who can access training */}
            {trainingNavItems.length > 0 && !isTraineeOnly && (
              <>
                {!collapsed && (
                  <div className="pt-4 pb-2">
                    <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Capacitación
                    </p>
                  </div>
                )}
                {collapsed && <Separator className="my-2" />}

                {trainingNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                      isActive(item.path) 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("w-5 h-5 flex-shrink-0", !isActive(item.path) && item.color)} />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                ))}
              </>
            )}

            {/* Admin Section */}
            {adminNavItems.length > 0 && (
              <>
                {!collapsed && (
                  <div className="pt-4 pb-2">
                    <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Administración
                    </p>
                  </div>
                )}
                {collapsed && <Separator className="my-2" />}

                {adminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                      isActive(item.path) 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                ))}
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User info */}
        {!collapsed && (
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{roleLabel[user?.role]}</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className={cn(
        "flex-1 flex flex-col min-h-screen transition-all duration-300",
        collapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        {/* Header */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-['Manrope'] hidden sm:block">
              {isTraineeOnly ? 'Inspired Training Academy' : 'Internal Credit Analysis'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleLanguage}
              data-testid="header-language-toggle"
            >
              <Languages className="w-5 h-5" />
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleDarkMode}
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu">
                  <User className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.full_name}</span>
                    <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {isTraineeOnly ? (
            <DLPProvider enableWatermark={true} enableRightClickProtection={true}>
              {children}
            </DLPProvider>
          ) : (
            children
          )}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
